import React, { useEffect, useState } from "react";
import axios, { API_BASE_URL } from "../../../components/api/axios";
import ImageLoader from "../../common/ImageLoader";
import "./About.css";

function About() {
  const [aboutData, setAboutData] = useState(null);

  // Fetch About Chapter data from backend
  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const res = await axios.get("/about-chapter");
        setAboutData(res.data);
      } catch (err) {
        console.error("❌ Failed to fetch About Chapter:", err);
      }
    };

    fetchAboutData();
  }, []);

  // Split description text into structured paragraphs
  const renderParagraphs = (description) => {
    if (!description) return null;
    if (description.includes("\n")) {
      return description
        .split(/\r?\n+/)
        .filter(Boolean)
        .map((paragraph, idx) => (
          <p key={idx} className="about-paragraph">
            {paragraph.trim()}
          </p>
        ));
    }
    return <p className="about-paragraph">{description}</p>;
  };

  // Render dynamic subtitle with line splitting if available
  const renderHeading = (subtitle) => {
    if (subtitle && subtitle.includes("&")) {
      const parts = subtitle.split("&");
      return (
        <>
          <span className="heading-white-line">{parts[0].trim()} &amp;</span>
          <span className="heading-blue-line">{parts.slice(1).join("&").trim()}</span>
        </>
      );
    }
    if (subtitle) {
      const words = subtitle.split(" ");
      if (words.length >= 4) {
        const mid = Math.ceil(words.length / 2);
        return (
          <>
            <span className="heading-white-line">{words.slice(0, mid).join(" ")}</span>
            <span className="heading-blue-line">{words.slice(mid).join(" ")}</span>
          </>
        );
      }
      return <span className="heading-blue-line">{subtitle}</span>;
    }
    return (
      <>
        <span className="heading-white-line">Inspiring Excellence &amp;</span>
        <span className="heading-blue-line">Driving Tech Innovation</span>
      </>
    );
  };

  return (
    <section id="about" className="about-chapter-section">
      {aboutData && (
        <div className="about-container">
          {/* Header Section Matching Design */}
          <div className="about-header-wrapper">
            <div className="about-tagline-bar">
              <span className="tagline-line line-left"></span>
              <span className="tagline-dot dot-left"></span>
              <span className="about-category-tag">ABOUT OUR CHAPTER</span>
              <span className="tagline-dot dot-right"></span>
              <span className="tagline-line line-right"></span>
            </div>

            <h2 className="about-title-main">
              Who <span className="blue-highlight">We</span> Are
            </h2>

            <div className="about-diamond-accent">
              <span className="diamond-symbol">◆</span>
            </div>

            <p className="about-header-desc">
              A community of learners, innovators, and future leaders in technology.
            </p>
          </div>

          {/* Main Glassmorphic Showcase Card */}
          <div className="about-showcase-card">
            {/* Left Column: Image with Dot Matrix & Floating Badge */}
            <div className="about-left-column">
              <div className="about-image-stage">
                {/* Decorative Dot Matrix on bottom-left */}
                <div className="about-dot-matrix-pattern"></div>

                {/* Main Photo Frame */}
                <div className="about-photo-wrapper">
                  <ImageLoader
                    src={`${API_BASE_URL}/${aboutData.imagePath}`}
                    alt="About DIET ACM Chapter"
                    className="about-primary-image"
                  />
                </div>

                {/* Floating Bottom Pill Badge */}
                <div className="about-floating-pill">
                  <div className="floating-pill-icon">
                    <i className="fas fa-users"></i>
                  </div>
                  <span className="floating-pill-label">
                    Building Connections. Creating Impact.
                  </span>
                </div>
              </div>
            </div>

            {/* Right Column: Mission Text & Stat Cards */}
            <div className="about-right-column">
              {/* Mission Heading with Compass Icon */}
              <div className="about-mission-heading-group">
                <div className="about-compass-icon-wrapper">
                  <svg
                    className="compass-vector-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#38bdf8"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"></polygon>
                  </svg>
                </div>

                <h3 className="about-mission-title">
                  {renderHeading(aboutData.subtitle)}
                </h3>
              </div>

              {/* Dynamic Description Paragraphs */}
              <div className="about-text-body">
                {renderParagraphs(aboutData.description)}
              </div>

              {/* Stat Cards Grid */}
              <div className="about-stats-container">
                {/* Active Members Card */}
                <div className="about-stat-card">
                  <div className="stat-icon-square members-bg">
                    <i className="fas fa-users"></i>
                  </div>
                  <div className="stat-info-body">
                    <h4 className="stat-count-number">
                      {aboutData.activeMembers || 0}+
                    </h4>
                    <span className="stat-name-label">Active Members</span>
                    <p className="stat-caption-note">
                      Passionate learners &amp; tech enthusiasts
                    </p>
                  </div>
                </div>

                {/* Events Conducted Card */}
                <div className="about-stat-card">
                  <div className="stat-icon-square events-bg">
                    <i className="fas fa-calendar-check"></i>
                  </div>
                  <div className="stat-info-body">
                    <h4 className="stat-count-number">
                      {aboutData.eventsOrganized || 0}+
                    </h4>
                    <span className="stat-name-label">Events Conducted</span>
                    <p className="stat-caption-note">
                      Workshops, talks, hackathons &amp; more
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default About;
